import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play, Sparkles, Zap, Target } from "lucide-react";
import { AppLink } from "./AppLink";

export function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950" />

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-400/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-violet-400/20 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-emerald-400/10 rounded-full blur-3xl animate-pulse delay-500" />

        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #6366F1 1px, transparent 1px), linear-gradient(to bottom, #6366F1 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
        <div className="text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-sm font-medium mb-8 animate-fade-in">
            <Sparkles className="h-4 w-4" />
            <span>La gestion de projet nouvelle génération</span>
          </div>

          {/* Main headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 dark:text-white mb-6 animate-fade-in">
            Organisez vos projets.
            <br />
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 bg-clip-text text-transparent">
              Pilotez votre charge.
            </span>
            <br />
            Gardez le contrôle.
          </h1>

          {/* Subheadline */}
          <p className="max-w-2xl mx-auto text-lg sm:text-xl text-slate-600 dark:text-slate-400 mb-10 animate-fade-in">
            Lapyno réunit tâches, charge équipe, temps et budget dans un seul outil — simple, rapide, et conçu pour les
            équipes qui veulent avancer.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-in">
            <Button
              size="lg"
              className="w-full sm:w-auto bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-xl shadow-indigo-500/30 text-lg px-8 py-6 group"
              asChild
            >
              <AppLink href="https://app.lapyno.com/?mode=signup">
                Essai gratuit 14 jours
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </AppLink>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="w-full sm:w-auto text-lg px-8 py-6 group border-slate-300 dark:border-slate-700"
            >
              <Play className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
              Voir la démo
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-slate-500 dark:text-slate-400 mb-16">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-500" />
              <span>Setup en 2 minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-emerald-500" />
              <span>Sans carte bancaire</span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-500" />
              <span>Annulez à tout moment</span>
            </div>
          </div>

          {/* Hero visual - App mockup with rabbit animation */}
          <div className="relative max-w-5xl mx-auto animate-fade-in">
            {/* Floating rabbit loader animation */}
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 z-10">
              <div className="relative">
                <RabbitLoader />
              </div>
            </div>

            {/* App mockup frame */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/20 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
              {/* Browser chrome */}
              <div className="flex items-center gap-2 px-4 py-3 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-amber-400" />
                  <div className="w-3 h-3 rounded-full bg-emerald-400" />
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="px-4 py-1 rounded-md bg-white dark:bg-slate-900 text-xs text-slate-400 border border-slate-200 dark:border-slate-700">
                    app.lapyno.com
                  </div>
                </div>
              </div>

              {/* App preview placeholder */}
              <div className="aspect-[16/10] bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center">
                <div className="text-center p-8">
                  {/* Simplified Kanban preview */}
                  <div className="flex gap-4 justify-center max-w-4xl mx-auto">
                    {["À faire", "En cours", "Terminé"].map((col, i) => (
                      <div key={col} className="flex-1 max-w-xs">
                        <div className="text-xs font-medium text-slate-500 mb-3 text-left">{col}</div>
                        <div className="space-y-2">
                          {[...Array(3 - i)].map((_, j) => (
                            <div
                              key={j}
                              className="p-3 bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 text-left"
                            >
                              <div className="h-2 w-3/4 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
                              <div className="h-2 w-1/2 bg-slate-100 dark:bg-slate-600 rounded" />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-2xl opacity-20 blur-xl" />
            <div className="absolute -top-4 -right-4 w-32 h-32 bg-gradient-to-br from-violet-500 to-purple-500 rounded-2xl opacity-20 blur-xl" />
          </div>
        </div>
      </div>
    </section>
  );
}

// Animated rabbit loader component
function RabbitLoader() {
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-full shadow-lg border border-slate-200 dark:border-slate-700">
      <svg viewBox="0 0 24 24" className="w-6 h-6 animate-bounce" style={{ animationDuration: "1s" }}>
        <defs>
          <linearGradient id="rabbit-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6366F1" />
            <stop offset="100%" stopColor="#8B5CF6" />
          </linearGradient>
        </defs>
        {/* Simplified rabbit silhouette */}
        <ellipse cx="12" cy="16" rx="6" ry="5" fill="url(#rabbit-grad)" />
        <ellipse cx="9" cy="7" rx="2" ry="5" fill="url(#rabbit-grad)" />
        <ellipse cx="15" cy="7" rx="2" ry="5" fill="url(#rabbit-grad)" />
      </svg>
      <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Chargement ultra-rapide...</span>
    </div>
  );
}
